// HelpButton — the "?" affordance + contextual help drawer.
//
// The drawer resolves the current route's help topic through the module
// registry (src/lib/helpTopics.ts): a topic file at src/help/topics/<key>.md
// makes the route contextual automatically. Routes without a topic degrade
// to the generic content below (admin links + escalation), so the button is
// always safe to place. Topics are precached by the service worker, so the
// drawer works fully offline.
//
// Place it in a page's sticky header next to the other icon actions. Do NOT
// float it — floating buttons steal the phone thumb zone (see the
// PalletSearchRoot note in App.tsx).

import { useEffect, useState } from 'react'
import { HelpCircle, ExternalLink, Send, ArrowLeft, GraduationCap } from 'lucide-react'
import clsx from 'clsx'
import { useLocation } from 'react-router-dom'
import GuardedLink from '@/components/GuardedLink'
import Modal from '@/components/Modal'
import { Skeleton } from '@/components/Skeleton'
import { openBugReport } from '@/components/BugReportForm'
import {
  COMMON_TOPIC_IDS, hasHelpTopic, loadHelpTopic, resolveHelpTopicId,
  type HelpTopic,
} from '@/lib/helpTopics'
import { logHelpEvent } from '@/lib/helpEvents'
import { offersTraining } from '@/lib/trainingGuides'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { useReturnHereState } from '@/lib/returnTo'
import { packhouseConfigHref } from '@/lib/packhouseConfigHref'

interface HelpButtonProps {
  /** Legacy per-page blurb heading — shown only when the route has no topic. */
  topic?:      string
  /** Legacy per-page blurb body — shown only when the route has no topic. */
  hint?:       string
  className?:  string
  /** What the trigger sits on — same prop name as <BackButton/> and
   *  <QueryStateGate/>. `light` swaps the faint gray icon and dark hover chip
   *  for tokens that read on a light toolbar. The drawer it opens is a
   *  <Modal/>, already a dark island, so it is unchanged. */
  surface?:    'shell' | 'light'
}

export default function HelpButton({ topic, hint, className, surface = 'shell' }: HelpButtonProps) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Help & support"
        aria-label="Help & support"
        className={clsx(
          'p-2 min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-xl transition-colors',
          surface === 'light'
            ? 'text-fg-muted hover:bg-raised-hover hover:text-fg'
            : 'hover:bg-surface text-fg-placeholder hover:text-white light:hover:text-fg',
          className,
        )}>
        <HelpCircle size={18} />
      </button>
      {open && <HelpDrawer legacyTopic={topic} legacyHint={hint} onClose={() => setOpen(false)} />}
    </>
  )
}

/**
 * The help drawer itself — exported so other surfaces (TeachingEmptyState's
 * "Learn more") can open it directly. `topicId` overrides the route-resolved
 * topic; omit it to show the current route's topic.
 */
export function HelpDrawer({ topicId, legacyTopic, legacyHint, onClose }: {
  topicId?:     string
  legacyTopic?: string
  legacyHint?:  string
  onClose:      () => void
}) {
  const { pathname, search } = useLocation()
  const { data: homeTenant } = useActiveTenant()
  const contextualId = resolveHelpTopicId(pathname, homeTenant?.kind, search)
  // A chip click pushes a topic on top of the contextual one; back returns.
  const [chipId, setChipId] = useState<string | null>(null)
  const activeId = chipId ?? topicId ?? contextualId

  // Telemetry: one drawer_open per open, with the topic it landed on. Covers
  // both entry points (contextual "?" and the ⌘K palette's help results, which
  // both render this drawer). Mount-only — chip navigation isn't a new open.
  useEffect(() => {
    logHelpEvent({ eventType: 'drawer_open', route: pathname, helpTopicId: topicId ?? contextualId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const chips = COMMON_TOPIC_IDS.filter(id => hasHelpTopic(id) && id !== activeId)

  // View-only drawer: no editable state, so no `snapshot` — the dirty guard
  // never fires and backdrop / X / Escape close straight away (as today).
  return (
    <Modal
      onClose={onClose}
      size="lg"
      noFooter
      title={
        <span className="flex items-center gap-2">
          {chipId ? (
            <button onClick={() => setChipId(null)} title="Back" aria-label="Back to the help index"
              className="tap-target p-1.5 -ml-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white">
              <ArrowLeft size={16} />
            </button>
          ) : (
            <HelpCircle size={18} className="text-blue-400 shrink-0" />
          )}
          <span className="truncate">Help & support</span>
        </span>
      }>
      <div className="space-y-4 text-sm">
        <TopicSection id={activeId} legacyTopic={legacyTopic} legacyHint={legacyHint} />

        {chips.length > 0 && (
          <section>
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Common questions</p>
            <div className="flex flex-wrap gap-2">
              {chips.map(id => <TopicChip key={id} id={id} onOpen={() => setChipId(id)} />)}
            </div>
          </section>
        )}

        {!chipId && <TrainingLink onGo={onClose} />}

        {!chipId && <AdminLinks />}

        {/* Delegates to the single global instance (BugReportRoot, mounted
            in App.tsx) rather than rendering a second copy — same shape as
            the ⌘K palette's "Report an app problem…". */}
        <EscalationSection onOpenTicket={openBugReport} />
      </div>
    </Modal>
  )
}

/** undefined = loading, null = no id / not found. */
function useHelpTopic(id: string | undefined): HelpTopic | null | undefined {
  const [topic, setTopic] = useState<HelpTopic | null | undefined>(id ? undefined : null)
  useEffect(() => {
    if (!id) { setTopic(null); return }
    let alive = true
    setTopic(undefined)
    loadHelpTopic(id).then(t => { if (alive) setTopic(t) })
    return () => { alive = false }
  }, [id])
  return topic
}

/** The drawer's top section: contextual topic, or the legacy blurb fallback. */
function TopicSection({ id, legacyTopic, legacyHint }: {
  id:           string | undefined
  legacyTopic?: string
  legacyHint?:  string
}) {
  const topic = useHelpTopic(id)
  if (!id) {
    // Legacy per-page blurb — only for pages that pass topic/hint and have
    // no markdown topic yet.
    if (!legacyTopic && !legacyHint) return null
    return (
      <section>
        {legacyTopic && <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">{legacyTopic}</p>}
        {legacyHint  && <p className="text-gray-300">{legacyHint}</p>}
      </section>
    )
  }
  if (topic === undefined) return (
    <div role="status">
      <span className="sr-only">Loading help…</span>
      <Skeleton className="h-4 w-2/3 rounded mb-3" />
      <Skeleton className="h-3 w-full rounded mb-2" />
      <Skeleton className="h-3 w-11/12 rounded mb-2" />
      <Skeleton className="h-3 w-4/5 rounded" />
    </div>
  )
  if (!topic) return null
  return (
    <section>
      <h2 className="text-base font-semibold text-white mb-2">{topic.title}</h2>
      <TopicBody html={topic.html} />
    </section>
  )
}

/** The way from a page's help to the whole library — on the apps that have one. */
function TrainingLink({ onGo }: { onGo: () => void }) {
  const { data: tenant } = useActiveTenant()
  if (!offersTraining(tenant?.kind)) return null
  return (
    <GuardedLink to="/training" onClick={onGo}
      className="flex items-center gap-2 rounded-lg border hairline-strong hover:border-gray-600 px-3 py-2">
      <GraduationCap size={14} className="text-sky-300 shrink-0" />
      <span className="flex-1">
        <span className="block text-white">Training — every page's guide and video</span>
        <span className="block text-[11px] text-muted">Step-by-step guides for your whole app, in one place.</span>
      </span>
    </GuardedLink>
  )
}

function AdminLinks() {
  return (
    <section className="border-t hairline pt-3">
      <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Common admin tasks</p>
      <ul className="space-y-2">
        <HelpLink to={packhouseConfigHref('health', 'Getting started')} withReturn label="Configure your packhouse"
          hint="PHC code, exporter code, address, target markets, customers, transporters, rate cards." />
        <HelpLink to="/users" label="Invite your team"
          hint="Scale operators, QC, dispatchers — every role they need to do their day." />
        <HelpLink to="/financial" label="Set rate cards"
          hint="Each pack-type's per-unit price drives the invoice line totals." />
        <HelpLink to="/audit" label="See who did what"
          hint="Soft-deletes + member changes across the last 7-90 days." />
        <HelpLink to="/activity" label="See real-time status changes"
          hint="The live operational feed — pallets packed / dispatched, deliveries received." />
      </ul>
    </section>
  )
}

function EscalationSection({ onOpenTicket }: { onOpenTicket: () => void }) {
  return (
    <section className="border-t hairline pt-3">
      <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Found a bug or have an idea?</p>
      <button
        type="button"
        onClick={onOpenTicket}
        className="w-full flex items-center gap-2 rounded-lg border hairline-strong hover:border-gray-600 px-3 py-2 text-left">
        <Send size={14} className="text-blue-300 shrink-0" />
        <div className="flex-1">
          <p className="text-white">Open a support ticket</p>
          <p className="text-[11px] text-gray-500">
            Reports a bug, asks a question, or suggests a feature — sent directly to us.
          </p>
        </div>
      </button>
      <p className="text-[11px] text-gray-500 mt-2">
        For urgent dispatch issues during pack season, WhatsApp your packhouse ops
        manager directly — tickets are best-effort response.
      </p>
    </section>
  )
}

function TopicChip({ id, onOpen }: { id: string; onOpen: () => void }) {
  const topic = useHelpTopic(id)
  if (!topic) return null
  return (
    <button
      type="button"
      onClick={onOpen}
      className="tap-target-y px-3 py-1.5 rounded-full border hairline-strong hover:border-gray-500 text-gray-300 hover:text-white text-xs">
      {topic.title}
    </button>
  )
}

/**
 * Rendered help prose. Also the body of every /training guide, whose manual
 * sections bring the h3s, tables and quotes a 20-line topic never has.
 */
export function TopicBody({ html }: { html: string }) {
  // html comes pre-rendered from loadHelpTopic / src/lib/trainingGuides.ts
  // (repo-authored content — same trust level as JSX, so no sanitiser).
  return (
    <div
      className={clsx(
        'leading-relaxed',
        '[&_p]:text-gray-300 [&_p]:mb-2',
        '[&_h2]:text-white [&_h2]:font-semibold [&_h2]:text-sm [&_h2]:uppercase [&_h2]:tracking-wider [&_h2]:mt-4 [&_h2]:mb-1.5',
        '[&_h3]:text-white [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1.5 [&_h4]:text-gray-200 [&_h4]:font-semibold [&_h4]:mt-3 [&_h4]:mb-1',
        '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2',
        '[&_li]:text-gray-300 [&_li]:mb-1',
        '[&_strong]:text-white [&_strong]:font-semibold',
        '[&_code]:text-blue-300 [&_code]:bg-gray-800 [&_code]:px-1 [&_code]:rounded',
        '[&_a]:text-blue-300 [&_a]:underline',
        '[&_blockquote]:border-l-2 [&_blockquote]:border-gray-600 [&_blockquote]:pl-3 [&_blockquote]:mb-2 [&_hr]:border-gray-700 [&_hr]:my-3',
        // A wide table scrolls inside itself, never the page.
        '[&_table]:block [&_table]:overflow-x-auto [&_table]:mb-2 [&_table]:text-xs',
        '[&_th]:text-left [&_th]:text-white [&_th]:font-semibold [&_th]:px-2 [&_th]:py-1 [&_th]:border-b [&_th]:border-gray-700',
        '[&_td]:text-gray-300 [&_td]:align-top [&_td]:px-2 [&_td]:py-1 [&_td]:border-b [&_td]:border-gray-800',
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function HelpLink({ to, label, hint, withReturn = false }: { to: string; label: string; hint: string; withReturn?: boolean }) {
  // withReturn: the destination's Back returns to the page the help was opened
  // on (Config reads it — F19 UF-10).
  const returnHere = useReturnHereState()
  return (
    <li>
      <GuardedLink to={to} state={withReturn ? returnHere : undefined} className="flex items-start gap-2 group hover:bg-gray-800/40 rounded-lg px-2 py-1.5 -mx-2">
        <ExternalLink size={12} className="text-gray-500 group-hover:text-blue-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-white group-hover:text-blue-200">{label}</p>
          <p className="text-[11px] text-gray-500 group-hover:text-gray-400">{hint}</p>
        </div>
      </GuardedLink>
    </li>
  )
}
